import { checkParams, resolveParams, type ParamSpecs } from '@flobi/artwork-core/plugin';
import { loadSandbox, PluginInstance } from '@flobi/artwork-core/plugin-host';
import { createContext, useCallback, useContext, useEffect, useRef, useState } from 'react';
import type { CanvasHandle, ToolId } from '../useEditorCanvas';
import { addonsApi, type AddonDetail, type AddonManifest, type AddonSummary } from './api';
import { addonTool, MISSES_ALLOWED } from './tool';

/**
 * Everything the Add-ons panel can do, held by the editor page.
 *
 * The panel is a view over this. Nothing here writes to the document except through the
 * canvas bridge, and nothing here evaluates an add-on except the sandbox: code arrives as
 * a string from the backend and goes straight into a QuickJS context.
 *
 * The two kinds keep different lifetimes, which is the design rather than a detail. A
 * command gets a fresh context per run and it is thrown away after — nothing survives,
 * which is a property worth having. A tool must hold a gesture between a press and a
 * release, so its context lives for as long as it is the add-on in hand.
 */

export interface Addons {
  readonly items: readonly AddonSummary[] | null;
  readonly problem: string | null;
  readonly notice: string | null;
  readonly busy: boolean;
  /** The add-on open in the panel, with its code and manifest. */
  readonly open: AddonDetail | null;
  readonly params: Record<string, unknown>;
  /** Which add-on's tool is in hand, if any — only while it IS the tool in hand. */
  readonly holding: string | null;
  /**
   * The name of the add-on in hand, for the dock's button. Not `open`'s: the panel can be
   * showing a different add-on, or none, while this one is held (QA 2026-09-24,
   * library-addons-F16).
   */
  readonly heldName: string | null;
  refresh(): Promise<void>;
  show(id: string | null): Promise<void>;
  setParam(name: string, value: unknown): void;
  run(): Promise<void>;
  hold(): Promise<void>;
  addFromCode(source: string): Promise<boolean>;
  /** A private copy of the open add-on, which is how you change one that is not yours. */
  copy(): Promise<void>;
  share(visibility: 'private' | 'workspace'): Promise<void>;
  remove(): Promise<void>;
  dismiss(): void;
}

export const AddonsContext = createContext<Addons | null>(null);

export function useAddonsContext(): Addons {
  const value = useContext(AddonsContext);
  if (!value) throw new Error('The Add-ons panel is only drawn inside the editor.');
  return value;
}

/** Why an add-on could not be read — said in terms of what the person can do about it. */
function reason(error: unknown): string {
  if (error instanceof Error) return error.message;
  return String(error);
}

export function useAddons(
  canvas: CanvasHandle,
  chooseTool: (id: ToolId) => void,
  fileOpen: boolean,
  /** The tool in hand, so the add-on counts as held only while it is that tool. */
  tool: ToolId,
  /**
   * Says something on screen whatever is open. A tool's problems go here rather than to
   * the panel's notice: the Library is usually closed while somebody draws, and a refused
   * drag or a timeout that dropped them back to Select explained nothing
   * (QA 2026-09-24, library-addons-F5).
   */
  say: (message: string) => void,
): Addons {
  const [items, setItems] = useState<readonly AddonSummary[] | null>(null);
  const [problem, setProblem] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [open, setOpen] = useState<AddonDetail | null>(null);
  const [paramsById, setParamsById] = useState<Record<string, Record<string, unknown>>>({});
  const [holding, setHolding] = useState<{ id: string; name: string } | null>(null);

  // Read by the tool while a hand is moving, which is not a render — so by ref.
  const paramsRef = useRef(paramsById);
  paramsRef.current = paramsById;
  const held = useRef<{ id: string; instance: PluginInstance } | null>(null);
  /**
   * Deadline misses per add-on, for the whole session.
   *
   * Kept here rather than in the tool: every pick-up builds a fresh tool, and the first
   * miss drops back to Select, so a count inside the tool was back to nought every time and
   * the "put down after three" safeguard could never fire (QA 2026-09-24, library-addons-F20).
   */
  const misses = useRef(new Map<string, number>());
  /** The latest `show` asked for; an answer to any earlier one is dropped (library-addons-F19). */
  const showing = useRef(0);
  /** Settings waiting to be saved, per add-on, so a dragged slider saves once (library-addons-F10). */
  const saving = useRef(new Map<string, ReturnType<typeof setTimeout>>());
  const sayRef = useRef(say);
  sayRef.current = say;

  // Put down whenever another tool is chosen — Select, a shape, or the fail-safe's drop
  // back to Select. `holding` used to outlive it, so the list went on saying In Hand
  // (QA 2026-09-24, library-addons-F15).
  useEffect(() => {
    if (tool !== 'addon') setHolding(null);
  }, [tool]);

  const refresh = useCallback(async () => {
    try {
      setItems(await addonsApi.list());
      setProblem(null);
    } catch (error) {
      setProblem(`Could not load add-ons: ${reason(error)}`);
    }
  }, []);

  useEffect(() => {
    if (fileOpen) void refresh();
  }, [fileOpen, refresh]);

  // Put the tool down, and close its context, when the editor goes away.
  useEffect(
    () => () => {
      held.current?.instance.dispose();
      held.current = null;
      for (const timer of saving.current.values()) clearTimeout(timer);
      saving.current.clear();
    },
    [],
  );

  const paramsOf = useCallback((detail: AddonDetail) => {
    const specs = (detail.version.manifest.params ?? {}) as ParamSpecs;
    return resolveParams(specs, paramsRef.current[detail.id] ?? detail.install?.params ?? {}) as Record<string, unknown>;
  }, []);

  const show = useCallback(
    async (id: string | null) => {
      setNotice(null);
      // Every call is the latest until another comes: the answer to an older one — a row
      // clicked before this one, or the reopening after Make a Copy or Shared with — used
      // to land after All Add-ons and open that add-on again (QA 2026-09-24,
      // library-addons-F19). show(null) counts, so nothing reopens after it.
      const asked = ++showing.current;
      if (!id) {
        setOpen(null);
        return;
      }
      setBusy(true);
      try {
        const detail = await addonsApi.get(id);
        if (asked !== showing.current) return;
        setOpen(detail);
        setParamsById((all) => ({ ...all, [id]: paramsOf(detail) }));
      } catch (error) {
        if (asked === showing.current) setProblem(`Could not open that add-on: ${reason(error)}`);
      } finally {
        setBusy(false);
      }
    },
    [paramsOf],
  );

  /**
   * Keeps the person's answers with their install, so they are there next time.
   *
   * Install is an upsert, so this makes the row when there is none. It used to return
   * early instead, and a library or workspace add-on — which never has one until then —
   * forgot its settings on every reload (QA 2026-09-24, library-addons-F10).
   */
  const remember = useCallback(async (detail: AddonDetail, params: Record<string, unknown>) => {
    try {
      await addonsApi.install(detail.id, params);
    } catch {
      // Losing a remembered answer is not worth interrupting anybody for.
    }
  }, []);

  /**
   * A tool's answers, saved a moment after the last change: the rail's slider sends every
   * value it passes through, and one save per tick is a request per tick. Commands save on
   * Run; a tool has no Run, so its settings were never saved at all (library-addons-F10).
   */
  const rememberSoon = useCallback(
    (detail: AddonDetail) => {
      const waiting = saving.current.get(detail.id);
      if (waiting) clearTimeout(waiting);
      saving.current.set(
        detail.id,
        setTimeout(() => {
          saving.current.delete(detail.id);
          void remember(detail, paramsRef.current[detail.id] ?? paramsOf(detail));
        }, 800),
      );
    },
    [remember, paramsOf],
  );

  const setParam = useCallback(
    (name: string, value: unknown) => {
      const id = open?.id ?? held.current?.id;
      if (!id) return;
      const next = { ...(paramsRef.current[id] ?? {}), [name]: value };
      paramsRef.current = { ...paramsRef.current, [id]: next };
      setParamsById((all) => ({ ...all, [id]: { ...(all[id] ?? {}), [name]: value } }));
      // A tool's form is saved as it changes; a command's on Run (library-addons-F10).
      if (open?.id === id && open.kind === 'tool') rememberSoon(open);
    },
    [open, rememberSoon],
  );

  const run = useCallback(async () => {
    if (!open || open.kind !== 'command') return;
    const bridge = canvas.addonBridge();
    if (!bridge) return;
    setBusy(true);
    setNotice(null);
    const params = paramsById[open.id] ?? paramsOf(open);
    let instance: PluginInstance | null = null;
    try {
      instance = await PluginInstance.open(open.version.code);
      const snapshot = bridge.snapshot(instance.manifest.reads);
      const out = instance.runCommand({ snapshot, host: bridge.host, params });
      const applied = bridge.apply(out, { pluginId: open.id, versionHash: open.version.hash, ms: out.ms });
      if (!applied.ok) setNotice(`${open.name} asked for something the page cannot do: ${applied.message}`);
      else if (applied.changed === 0) setNotice(out.logs[0] ?? `${open.name} ran and changed nothing.`);
      else setNotice(`${open.name} changed ${applied.changed} ${applied.changed === 1 ? 'layer' : 'layers'}. ⌘Z takes it back.`);
      void remember(open, params);
    } catch (error) {
      setNotice(`${open.name} failed: ${reason(error)}`);
    } finally {
      // A command keeps nothing between runs. Its context goes as soon as it is done.
      instance?.dispose();
      setBusy(false);
    }
  }, [open, canvas, paramsById, paramsOf, remember]);

  const hold = useCallback(async () => {
    if (!open || open.kind !== 'tool') return;
    // Put down for the session after too many timeouts, as the tool says when it drops
    // (library-addons-F20).
    if ((misses.current.get(open.id) ?? 0) >= MISSES_ALLOWED) {
      setNotice(`${open.name} kept running too long and has been put down for this session.`);
      return;
    }
    setBusy(true);
    setNotice(null);
    try {
      await loadSandbox();
      const instance = await PluginInstance.open(open.version.code);
      const detail = open;
      const tool = addonTool({
        pluginId: detail.id,
        versionHash: detail.version.hash,
        instance,
        bridge: () => canvas.addonBridge(),
        params: () => paramsRef.current[detail.id] ?? paramsOf(detail),
        setParam: (name, value) => {
          const next = { ...(paramsRef.current[detail.id] ?? paramsOf(detail)), [name]: value };
          paramsRef.current = { ...paramsRef.current, [detail.id]: next };
          setParamsById((all) => ({ ...all, [detail.id]: { ...(all[detail.id] ?? {}), [name]: value } }));
          rememberSoon(detail);
        },
        onProblem: (message) => sayRef.current(message),
        missed: () => {
          const count = (misses.current.get(detail.id) ?? 0) + 1;
          misses.current.set(detail.id, count);
          return count;
        },
      });
      // The previous add-on's context goes when a new one takes the slot.
      held.current?.instance.dispose();
      held.current = { id: detail.id, instance };
      canvas.setAddonTool(tool);
      chooseTool('addon');
      setHolding({ id: detail.id, name: detail.name });
    } catch (error) {
      setNotice(`${open.name} could not be picked up: ${reason(error)}`);
    } finally {
      setBusy(false);
    }
  }, [open, canvas, chooseTool, paramsOf, rememberSoon]);

  const addFromCode = useCallback(
    async (source: string): Promise<boolean> => {
      setBusy(true);
      setNotice(null);
      let instance: PluginInstance | null = null;
      try {
        // The manifest is read by the sandbox, not parsed out of the text: the only way
        // to know what an add-on declares is to let it declare it, somewhere it can do
        // nothing else.
        instance = await PluginInstance.open(source);
        const manifest = instance.manifest as unknown as AddonManifest;
        // Its options are checked before it is kept: a malformed list saved without
        // complaint and then threw every time the panel drew it.
        checkParams(manifest.params ?? {});
        const made = await addonsApi.create({ source, code: source, manifest });
        await refresh();
        await show(made.id);
        setNotice(`${manifest.name} is added and switched on.`);
        return true;
      } catch (error) {
        setNotice(`That is not an add-on yet: ${reason(error)}`);
        return false;
      } finally {
        instance?.dispose();
        setBusy(false);
      }
    },
    [refresh, show],
  );

  const copy = useCallback(async () => {
    if (!open) return;
    // The same source, added as a new add-on of this person's own. The original — a
    // library row, or a colleague's — is not touched, and nobody else sees the copy.
    await addFromCode(open.version.code);
  }, [open, addFromCode]);

  const share = useCallback(
    async (visibility: 'private' | 'workspace') => {
      if (!open) return;
      try {
        await addonsApi.share(open.id, visibility);
        setNotice(visibility === 'workspace' ? `${open.name} is shared with your workspace.` : `${open.name} is private again.`);
        await refresh();
        await show(open.id);
      } catch (error) {
        setNotice(`Could not change who sees it: ${reason(error)}`);
      }
    },
    [open, refresh, show],
  );

  const remove = useCallback(async () => {
    // Busy while it goes, so a second press cannot send a second delete (library-addons-F11).
    if (!open || busy) return;
    setBusy(true);
    try {
      await addonsApi.remove(open.id);
      if (held.current?.id === open.id) {
        canvas.setAddonTool(null);
        held.current.instance.dispose();
        held.current = null;
        setHolding(null);
        chooseTool('select');
      }
      setOpen(null);
      await refresh();
      setNotice(`${open.name} is gone. Anything it made is still on the page.`);
    } catch (error) {
      setNotice(`Could not remove it: ${reason(error)}`);
    } finally {
      setBusy(false);
    }
  }, [open, busy, canvas, chooseTool, refresh]);

  return {
    items,
    problem,
    notice,
    busy,
    open,
    params: open ? (paramsById[open.id] ?? {}) : {},
    holding: holding?.id ?? null,
    heldName: holding?.name ?? null,
    refresh,
    show,
    setParam,
    run,
    hold,
    addFromCode,
    copy,
    share,
    remove,
    dismiss: () => setNotice(null),
  };
}
